import { supabase } from '@/integrations/supabase/client';

export interface RealAIInsight {
  id: string;
  type: 'performance' | 'engagement' | 'content' | 'prediction';
  title: string;
  description: string;
  confidence: number;
  impact: 'high' | 'medium' | 'low';
  actionable: boolean;
  created_at: string;
  user_count?: number;
}

export interface AISystemMetrics {
  totalInsights: number;
  activeModels: number;
  accuracyRate: number;
  lastAnalysis: string;
  hasRealData: boolean;
  dataFreshness: 'fresh' | 'stale' | 'none';
}

export const aiAnalyticsService = {
  async getSystemMetrics(): Promise<AISystemMetrics> {
    try {
      // Get total insights count
      const { count: insightsCount } = await supabase
        .from('ai_insights')
        .select('*', { count: 'exact', head: true });

      // Get learning patterns count (proxy for active models)
      const { count: patternsCount } = await supabase
        .from('learning_patterns')
        .select('*', { count: 'exact', head: true });

      // Get average confidence score as accuracy proxy
      const { data: avgConfidence } = await supabase
        .from('ai_insights')
        .select('confidence_score')
        .not('confidence_score', 'is', null);

      const hasConfidenceData = avgConfidence && avgConfidence.length > 0;
      const accuracyRate = hasConfidenceData 
        ? avgConfidence.reduce((sum, insight) => sum + (insight.confidence_score || 0), 0) / avgConfidence.length
        : 0;

      // Get latest insight timestamp
      const { data: latestInsight } = await supabase
        .from('ai_insights')
        .select('created_at')
        .order('created_at', { ascending: false })
        .limit(1);

      const lastAnalysis = latestInsight?.[0]?.created_at 
        ? new Date(latestInsight[0].created_at).toLocaleString()
        : 'Never';

      // Determine data freshness
      const hasRealData = (insightsCount || 0) > 0 || (patternsCount || 0) > 0;
      let dataFreshness: 'fresh' | 'stale' | 'none' = 'none';
      
      if (latestInsight?.[0]?.created_at) {
        const lastUpdate = new Date(latestInsight[0].created_at);
        const hoursSinceUpdate = (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60);
        dataFreshness = hoursSinceUpdate < 24 ? 'fresh' : 'stale';
      }

      return {
        totalInsights: insightsCount || 0,
        activeModels: patternsCount || 0, // Removed artificial minimum
        accuracyRate: Math.round(accuracyRate * 10) / 10, // Removed default fallback
        lastAnalysis,
        hasRealData,
        dataFreshness
      };
    } catch (error) {
      console.error('Error fetching AI system metrics:', error);
      // Return transparent empty state instead of fake defaults
      return {
        totalInsights: 0,
        activeModels: 0,
        accuracyRate: 0,
        lastAnalysis: 'Error loading data',
        hasRealData: false,
        dataFreshness: 'none'
      };
    }
  },

  async getAdminInsights(): Promise<RealAIInsight[]> {
    try {
      const { data: insights, error } = await supabase
        .from('ai_insights')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      // Transform database insights to display format
      const transformedInsights: RealAIInsight[] = (insights || []).map(insight => ({
        id: insight.id,
        type: this.mapInsightType(insight.insight_type),
        title: this.generateInsightTitle(insight),
        description: this.generateInsightDescription(insight),
        confidence: insight.confidence_score || 0,
        impact: this.determineImpact(insight.confidence_score || 0),
        actionable: true,
        created_at: insight.created_at,
        user_count: this.extractUserCount(insight.insight_data)
      }));

      return transformedInsights;
    } catch (error) {
      console.error('Error fetching admin AI insights:', error);
      return [];
    }
  },

  mapInsightType(dbType: string): 'performance' | 'engagement' | 'content' | 'prediction' {
    switch (dbType?.toLowerCase()) {
      case 'performance': return 'performance';
      case 'engagement': return 'engagement';
      case 'prediction': return 'prediction';
      default: return 'content';
    }
  },

  generateInsightTitle(insight: any): string {
    const category = insight.insight_category || 'General';
    const type = insight.insight_type || 'Analysis';
    return `${category} ${type} Insight`;
  },

  generateInsightDescription(insight: any): string {
    if (insight.insight_data?.description) {
      return insight.insight_data.description;
    }
    
    const category = insight.insight_category || 'learning';
    const confidence = insight.confidence_score || 0;
    return `AI analysis detected patterns in ${category} with ${confidence.toFixed(0)}% confidence.`;
  },

  determineImpact(confidence: number): 'high' | 'medium' | 'low' {
    if (confidence >= 80) return 'high';
    if (confidence >= 60) return 'medium';
    return 'low';
  },

  extractUserCount(insightData: any): number | undefined {
    if (insightData?.user_count) return insightData.user_count;
    if (insightData?.affected_users) return insightData.affected_users;
    return undefined;
  }
};
